/**
 * Constructeurs des blocs du panneau « Synchronisation » et de l'écran de consentement.
 * Composants existants uniquement : `.btn`, `.card`, `.info-banner`, `table.data`, `.nx-update-bar`, `.sr-only`.
 */
import { getIssuerInfo } from '@infra/legal/IssuerInfoService';
import type { SyncStatus } from '@infra/sync/hybrid/HybridSyncEngine';
import type { SyncAttentionItem } from '@infra/sync/hybrid/SyncBridgeTypes';
import type { SyncLabel, SyncTone } from '@application/sync/HybridSyncLabels';
import { h, prefersReducedMotion } from '@presentation/shell/HybridSyncDom';
import type { InitialSummary } from '@presentation/shell/HybridSyncNotices';
import { initialSummaryText } from '@presentation/shell/HybridSyncNotices';
import {
  CONSENT_FIRST_SEND_PARAGRAPHS,
  CONSENT_FIRST_SEND_TITLE,
  buildConsentPracticalPoints,
  TEXT,
  itemHelp,
  itemReason,
  itemTitle,
} from '@presentation/shell/HybridSyncText';

export const TONE_VAR: Readonly<Record<SyncTone, string>> = {
  ok: 'var(--success)',
  info: 'var(--teal)',
  warn: 'var(--warning)',
  error: 'var(--danger)',
};

export function formatWhen(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' });
}

/** Pastille de couleur décorative : la nature de l'état est toujours aussi écrite en toutes lettres. */
export function toneDot(tone: SyncTone): HTMLElement {
  return h('span', {
    class: 'hsync-dot',
    style: `display:inline-block;width:10px;height:10px;border-radius:50%;flex-shrink:0;background:${TONE_VAR[tone]};`,
    attrs: { 'aria-hidden': 'true' },
  });
}

/** Barre d'avancement (classes `.nx-update-*` existantes). Sans valeur connue : barre « en cours » discrète. */
export function buildProgressBar(
  progress: { done: number; total: number } | null,
  ariaLabel: string,
  compact = false,
): HTMLElement {
  const known = progress !== null && progress.total > 0;
  const pct = known ? Math.min(100, Math.round((progress.done / progress.total) * 100)) : 0;
  const attrs: Record<string, string> = {
    role: 'progressbar',
    'aria-label': ariaLabel,
    'aria-valuemin': '0',
    'aria-valuemax': '100',
  };
  if (known) {
    attrs['aria-valuenow'] = String(pct);
    attrs['aria-valuetext'] = `${progress.done} sur ${progress.total}`;
  }
  const fill = h('div', { class: 'nx-update-bar-fill' });
  if (known) fill.style.width = `${pct}%`;
  else if (prefersReducedMotion()) fill.style.width = '38%';
  else fill.classList.add('is-indeterminate');
  const bar = h('div', { class: 'nx-update-bar', attrs, style: compact ? 'height:4px;width:44px;flex-shrink:0;' : '' }, fill);
  return bar;
}

export interface StateBlockArgs {
  label: SyncLabel | null;
  status: SyncStatus | null;
  onReconnect: () => void;
  onUpdate: () => void;
}

export function showsProgress(status: SyncStatus | null): boolean {
  return Boolean(status && (status.progress || (status.state === 'syncing' && status.phase !== 'live')));
}

/** État courant en clair + phrase d'explication + action utile selon l'état. */
export function buildStateBlock(args: StateBlockArgs): HTMLElement {
  const { label, status } = args;
  const root = h('div', { attrs: { 'data-region': 'state' } });
  if (!label || !status) {
    root.append(
      h('p', { id: 'hybrid-sync-loading', class: 'info-banner', attrs: { role: 'status', 'aria-busy': 'true' } }, TEXT.loadingState),
    );
    return root;
  }
  root.append(
    h(
      'p',
      { style: 'display:flex;align-items:center;gap:8px;font-size:15px;color:var(--marine);margin-bottom:6px;' },
      toneDot(label.tone),
      h('strong', { id: 'hybrid-sync-state-text' }, label.text),
    ),
    h(
      'p',
      { class: 'info-banner', style: `border-left-color:${TONE_VAR[label.tone]};margin-bottom:12px;` },
      h('span', { id: 'hybrid-sync-state-detail' }, label.detail),
    ),
  );
  if (status.state === 'needs_login') {
    const btn = h('button', { type: 'button', id: 'hybrid-sync-reconnect', class: 'btn btn-primary btn-sm', style: 'margin-bottom:12px;', attrs: { 'data-focus-key': 'reconnect' } }, TEXT.reconnect);
    btn.addEventListener('click', args.onReconnect);
    root.append(btn);
  }
  if (status.state === 'paused_upgrade') {
    const btn = h('button', { type: 'button', id: 'hybrid-sync-update', class: 'btn btn-primary btn-sm', style: 'margin-bottom:12px;', attrs: { 'data-focus-key': 'update' } }, TEXT.update);
    btn.addEventListener('click', args.onUpdate);
    root.append(btn);
  }
  if (showsProgress(status)) {
    const progress = status.progress;
    const text = progress && progress.total > 0 ? `${progress.done} sur ${progress.total}` : 'Échange en cours';
    root.append(
      h(
        'div',
        { id: 'hybrid-sync-progress', style: 'margin-bottom:14px;' },
        h('p', { style: 'font-size:13px;color:var(--marine);margin-bottom:6px;' }, status.phase !== 'live' ? TEXT.firstSendRunning : 'Échange des modifications avec le serveur.'),
        buildProgressBar(progress, TEXT.firstSendTitle),
        h('p', { id: 'hybrid-sync-progress-text', style: 'font-size:12px;margin-top:4px;' }, text),
      ),
    );
  }
  return root;
}

export function buildSummaryBlock(summary: InitialSummary | null): HTMLElement | null {
  if (!summary) return null;
  return h(
    'section',
    { id: 'hybrid-sync-summary', class: 'info-banner', style: 'flex-direction:column;gap:4px;', attrs: { 'aria-labelledby': 'hybrid-sync-summary-title' } },
    h('strong', { id: 'hybrid-sync-summary-title' }, TEXT.firstSendSummaryTitle),
    h('span', {}, initialSummaryText(summary)),
    summary.identical > 0 ? h('span', {}, `${summary.identical} fiche${summary.identical > 1 ? 's' : ''} identique${summary.identical > 1 ? 's' : ''} sur le poste et sur le serveur.`) : null,
    summary.replaced > 0 ? h('span', {}, 'Pour chaque fiche différente, la version du serveur a été gardée. Votre copie est dans « À examiner ».') : null,
  );
}

export function buildCountersBlock(status: SyncStatus | null, attentionCount: number): HTMLElement {
  const row = (term: string, value: string, id: string): DocumentFragment => {
    const frag = document.createDocumentFragment();
    frag.append(h('dt', {}, term), h('dd', { id, style: 'margin:0;color:var(--marine);font-weight:600;' }, value));
    return frag;
  };
  return h(
    'dl',
    { id: 'hybrid-sync-counters', style: 'display:grid;grid-template-columns:auto 1fr;gap:4px 16px;margin:0 0 14px;font-size:13px;' },
    row('Modifications en attente', String(status?.pendingCount ?? 0), 'hybrid-sync-count-pending'),
    row('À examiner', String(attentionCount), 'hybrid-sync-count-attention'),
    row('Dernière synchronisation réussie', formatWhen(status?.lastSuccessAt), 'hybrid-sync-last-success'),
    row('Dernière réception', formatWhen(status?.lastPullAt), 'hybrid-sync-last-pull'),
  );
}

export type AttentionLoad = 'loading' | 'ready' | 'error';

export interface AttentionArgs {
  load: AttentionLoad;
  items: readonly SyncAttentionItem[];
  busyId: string | null;
  onAction: (index: number, action: 'abandon' | 'retry' | 'copied') => void;
  onReload: () => void;
}

export function attentionLabel(item: SyncAttentionItem): string {
  return `${itemTitle(item)}, fiche ${item.entity_id}`;
}

function actionButton(
  cls: string,
  text: string,
  key: string,
  dataName: string,
  index: number,
  item: SyncAttentionItem,
  busy: boolean,
  run: () => void,
): HTMLButtonElement {
  const btn = h('button', {
    type: 'button',
    class: `btn btn-sm ${cls}`,
    disabled: busy,
    attrs: { [dataName]: String(index), 'data-focus-key': `${key}-${item.id}`, 'aria-label': `${text} : ${attentionLabel(item)}` },
  }, text);
  btn.addEventListener('click', run);
  return btn;
}

/** Section « À examiner » : titre, explication et tableau (ou message de chargement, d'erreur, de liste vide). */
export function buildAttentionSection(args: AttentionArgs): HTMLElement {
  const { load, items } = args;
  const heading = h('h3', { id: 'hybrid-sync-attention-heading', style: 'font-size:15px;color:var(--marine);margin:0 0 6px;', attrs: { tabindex: '-1', 'data-focus-key': 'attention-heading' } }, TEXT.attentionHeading);
  const section = h('section', { attrs: { 'aria-labelledby': 'hybrid-sync-attention-heading', 'data-region': 'attention' } }, heading);
  if (load === 'loading') {
    section.append(h('p', { id: 'hybrid-sync-attention-loading', attrs: { role: 'status', 'aria-busy': 'true' } }, TEXT.loadingList));
    return section;
  }
  if (load === 'error') {
    const retry = h('button', { type: 'button', id: 'hybrid-sync-attention-reload', class: 'btn btn-secondary btn-sm', attrs: { 'data-focus-key': 'reload' } }, TEXT.retry);
    retry.addEventListener('click', args.onReload);
    section.append(
      h('p', { id: 'hybrid-sync-attention-error', class: 'info-banner', style: 'border-left-color:var(--danger);', attrs: { role: 'alert' } }, TEXT.listError),
      retry,
    );
    return section;
  }
  if (items.length === 0) {
    section.append(h('p', { id: 'hybrid-sync-attention-empty', class: 'empty', style: 'padding:20px 12px;color:var(--ardoise);' }, TEXT.listEmpty));
    return section;
  }
  section.append(h('p', { style: 'font-size:13px;margin-bottom:10px;' }, TEXT.attentionIntro));
  const body = h('tbody');
  items.forEach((item, index) => {
    const busy = args.busyId === item.id;
    const buttons: HTMLButtonElement[] = [];
    if (item.kind === 'rejected_entry' && item.action === 'upsert' && item.code !== 'payslip_locked') {
      buttons.push(actionButton('btn-secondary', TEXT.retry, 'retry', 'data-attn-retry', index, item, busy, () => args.onAction(index, 'retry')));
    }
    if (item.payload && item.resource !== 'tenant-profile') {
      buttons.push(actionButton('btn-secondary', TEXT.copy, 'copy', 'data-attn-copy', index, item, busy, () => args.onAction(index, 'copied')));
    }
    buttons.push(actionButton('btn-ghost', TEXT.abandon, 'abandon', 'data-attn-abandon', index, item, busy, () => args.onAction(index, 'abandon')));
    body.append(
      h(
        'tr',
        { attrs: { 'data-attn-idx': String(index) } },
        h(
          'td',
          { style: 'vertical-align:top;color:var(--marine);' },
          h('strong', {}, itemTitle(item)),
          h('div', { style: 'font-size:12px;color:var(--ardoise);' }, 'Fiche : ', h('code', { class: 'mono' }, item.entity_id)),
        ),
        h('td', { style: 'vertical-align:top;white-space:nowrap;' }, formatWhen(item.at)),
        h(
          'td',
          { style: 'vertical-align:top;' },
          h('span', { style: 'color:var(--marine);' }, h('strong', {}, 'Motif : '), itemReason(item)),
          h('div', { style: 'font-size:12px;margin-top:2px;' }, itemHelp(item)),
        ),
        h('td', { style: 'vertical-align:top;' }, h('div', { style: 'display:flex;gap:6px;flex-wrap:wrap;' }, ...buttons)),
      ),
    );
  });
  section.append(
    h(
      'div',
      // `contain:inline-size` : le tableau défile dans son cadre au lieu d'élargir la fenêtre sur un petit écran.
      { style: 'overflow-x:auto;contain:inline-size;' },
      h(
        'table',
        { class: 'data', id: 'hybrid-sync-attention-table' },
        h('caption', { class: 'sr-only' }, 'Modifications à examiner'),
        h('thead', {}, h('tr', {}, ...['Fiche', 'Date', 'Motif', 'Actions'].map((t) => h('th', { attrs: { scope: 'col' } }, t)))),
        body,
      ),
    ),
  );
  return section;
}

export interface ConsentArgs {
  canConsent: boolean;
  message: string;
  paragraphs: readonly string[];
  checkboxText: string;
  onAccept: () => void;
}

/** Écran de consentement : texte, premier envoi, durée / hébergement / contact, case obligatoire. */
export function buildConsentContent(args: ConsentArgs): { content: HTMLElement; accept: HTMLButtonElement | null; check: HTMLInputElement | null } {
  const content = h('div', { id: 'hybrid-sync-consent', style: 'font-size:13px;line-height:1.5;color:var(--marine);' });
  content.append(
    h('h3', { style: 'font-size:15px;margin:0 0 8px;' }, 'Ce que fait la synchronisation'),
    ...args.paragraphs.map((p) => h('p', { style: 'margin:0 0 10px;' }, p)),
    h('h3', { id: 'hybrid-sync-first-send-title', style: 'font-size:15px;margin:16px 0 8px;' }, CONSENT_FIRST_SEND_TITLE),
    ...CONSENT_FIRST_SEND_PARAGRAPHS.map((p) => h('p', { style: 'margin:0 0 10px;' }, p)),
  );
  for (const point of buildConsentPracticalPoints(getIssuerInfo())) {
    content.append(h('h3', { style: 'font-size:15px;margin:16px 0 8px;' }, point.title), h('p', { style: 'margin:0 0 10px;' }, point.text));
  }
  if (!args.canConsent) {
    content.append(h('p', { id: 'hybrid-sync-consent-info', class: 'info-banner', style: 'margin-top:14px;', attrs: { role: 'status' } }, args.message || TEXT.notAdmin));
    return { content, accept: null, check: null };
  }
  const check = h('input', { type: 'checkbox', id: 'hybrid-sync-consent-check', style: 'margin-top:3px;flex-shrink:0;' });
  const accept = h('button', { type: 'button', id: 'hybrid-sync-consent-accept', class: 'btn btn-primary', disabled: true }, TEXT.accept);
  check.addEventListener('change', () => {
    accept.disabled = !check.checked;
  });
  accept.addEventListener('click', args.onAccept);
  content.append(
    h(
      'label',
      { style: 'display:flex;gap:10px;align-items:flex-start;margin:16px 0 12px;cursor:pointer;', attrs: { for: 'hybrid-sync-consent-check' } },
      check,
      h('span', {}, args.checkboxText),
    ),
    h('div', { style: 'display:flex;gap:8px;' }, accept),
    h('p', { id: 'hybrid-sync-consent-error', style: 'color:var(--danger);font-size:13px;margin-top:8px;', hidden: true, attrs: { role: 'alert' } }),
  );
  return { content, accept, check };
}

/** Texte de la pastille quand elle montre l'avancement du premier envoi. */
export function pillText(label: SyncLabel, status: SyncStatus | null): string {
  const progress = status?.progress;
  if (status?.state === 'syncing' && progress && progress.total > 0) return `${label.text} ${progress.done} sur ${progress.total}`;
  return label.text;
}
